import loadable from "@loadable/component"
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { shallowEqual, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import { retry } from "helpers/application"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

import DesktopNav from "./desktop-nav"
import MobileHeader from "./mobile-header"

const CountdownBanner = loadable(() =>
  retry(() => import("areas/header/countdown-banner"))
)

const HeaderContent = () => {
  const microbanner = useSelector(
    (state) => state.sale.microbanner,
    shallowEqual
  )
  const secondMicroBanner = useSelector(
    (state) => state.sale.second_microbanner,
    shallowEqual
  )

  const [sticky, setSticky] = useState(false)
  const [hideStickyNav, setHideStickyNav] = useState(false)
  const headerRef = useRef()
  const rafRef = useRef()
  const lastScrollYRef = useRef(0)
  const isTickingRef = useRef(false)
  const headerHeightRef = useRef(0)
  const location = useLocation()
  const locationRef = useRef(location)

  const { isMobile } = useWindowDimensions()

  const shouldHideNavOnScrollDown = useMemo(
    () => isMobile && location.pathname.includes("/products/"),
    [isMobile, location.pathname]
  )

  const shouldHideRef = useRef(shouldHideNavOnScrollDown)

  // Keep refs updated for the persistent scroll listener
  useEffect(() => {
    locationRef.current = location
    shouldHideRef.current = shouldHideNavOnScrollDown
  }, [location, shouldHideNavOnScrollDown])

  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      headerHeightRef.current = headerRef.current.offsetHeight
    }
  }, [])

  // Initial measurement and resize listener
  useEffect(() => {
    updateHeaderHeight()
    window.addEventListener("resize", updateHeaderHeight)
    return () => window.removeEventListener("resize", updateHeaderHeight)
  }, [updateHeaderHeight])

  useEffect(() => {
    const handleScroll = () => {
      if (!isTickingRef.current) {
        isTickingRef.current = true
        rafRef.current = window.requestAnimationFrame(() => {
          if (headerRef.current) {
            const currentScrollY = window.scrollY
            const headerHeight = headerHeightRef.current

            setSticky((prev) => {
              const next =
                !locationRef.current.pathname.includes("cart") &&
                currentScrollY > headerHeight + 50
              return prev === next ? prev : next
            })

            if (shouldHideRef.current) {
              setHideStickyNav((prev) => {
                const next =
                  currentScrollY < lastScrollYRef.current &&
                  (prev || currentScrollY > headerHeight * 2)
                return prev === next ? prev : next
              })
            }

            lastScrollYRef.current = currentScrollY
          }
          isTickingRef.current = false
        })
      }
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => {
      window.cancelAnimationFrame(rafRef.current)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  return (
    <header
      className={cn(
        "backface-hidden border-b-grey-10 duration-400 top-0 z-40 flex w-full flex-col bg-white transition-all ease-out",
        shouldHideNavOnScrollDown
          ? hideStickyNav
            ? "sticky translate-y-0"
            : sticky && "-translate-y-full"
          : "sticky",
        sticky ? "shadow-lg" : "shadow-none",
        location.pathname.includes("cart") && "tablet:static"
      )}
      ref={headerRef}
    >
      <CountdownBanner microbanner={microbanner} />
      <MobileHeader />
      <DesktopNav />
      {secondMicroBanner && (
        <CountdownBanner
          isSecondMicroBanner
          microbanner={secondMicroBanner}
          // showOnMobile={!shouldSimplifyAboveFoldOnMobile}
        />
      )}
    </header>
  )
}

export default memo(HeaderContent)
